import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { addComment } from '../api/addComment'
import { fetchPostDetail, type PostDetail } from '../api/fetchPostDetail'
import { votePost } from '../api/votePost'
import { StatusPill } from '../components/StatusPill'
import { VoteButton } from '../components/VoteButton'
import { formatRelativeTime } from '../lib/formatRelativeTime'

export const PostDetailPage = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  const postId = id ?? ''
  const queryClient = useQueryClient()
  const [commentBody, setCommentBody] = useState('')
  const [authorName, setAuthorName] = useState('')

  const detailKey = ['post', postId] as const

  const detailQuery = useQuery({
    queryKey: detailKey,
    queryFn: () => fetchPostDetail({ postId }),
    enabled: postId.length > 0,
  })

  const voteMutation = useMutation({
    mutationFn: () => votePost({ postId }),
    onSuccess: (result) => {
      queryClient.setQueryData<PostDetail>(detailKey, (old) =>
        old
          ? {
              ...old,
              post: {
                ...old.post,
                hasVoted: result.hasVoted,
                voteCount: result.voteCount,
              },
            }
          : old,
      )
    },
  })

  const commentMutation = useMutation({
    mutationFn: () =>
      addComment({
        postId,
        body: commentBody,
        authorName: authorName.trim() ? authorName.trim() : null,
      }),
    onSuccess: () => {
      setCommentBody('')
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-10">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 font-display text-sm text-dune-text-secondary transition-colors hover:text-dune-text"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar ao roadmap
      </Link>

      {detailQuery.isLoading ? (
        <p className="py-12 text-center text-sm text-dune-text-secondary">
          Carregando...
        </p>
      ) : null}

      {detailQuery.isError ? (
        <p className="py-12 text-center text-sm text-signal-red">
          Ideia não encontrada.
        </p>
      ) : null}

      {detailQuery.data ? (
        <div className="mt-6">
          <div className="flex items-start gap-4 rounded-2xl border border-dune-border bg-white p-5 sm:p-6">
            <VoteButton
              voteCount={detailQuery.data.post.voteCount}
              hasVoted={detailQuery.data.post.hasVoted}
              pending={voteMutation.isPending}
              onVote={() => voteMutation.mutate()}
            />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-xl font-medium tracking-tight text-dune-text sm:text-2xl">
                  {detailQuery.data.post.title}
                </h1>

                <StatusPill status={detailQuery.data.post.status} />
              </div>

              {detailQuery.data.post.description ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-dune-text-secondary">
                  {detailQuery.data.post.description}
                </p>
              ) : null}
            </div>
          </div>

          <section className="mt-8">
            <h2 className="font-display text-xs uppercase tracking-wider text-dune-text-secondary">
              Comentários · {detailQuery.data.comments.length}
            </h2>

            <form
              className="mt-4 flex flex-col gap-3 rounded-2xl border border-dune-border bg-white p-4 sm:p-5"
              onSubmit={(event) => {
                event.preventDefault()
                commentMutation.mutate()
              }}
            >
              <input
                value={authorName}
                onChange={(event) => setAuthorName(event.target.value)}
                placeholder="Seu nome (opcional)"
                maxLength={80}
                className="h-11 w-full rounded-2xl border border-dune-border bg-white px-4 font-sans text-sm text-dune-text outline-none placeholder:text-[#C4C4CC] focus:border-dune-accent focus:ring-2 focus:ring-dune-accent/20"
              />
              <textarea
                value={commentBody}
                onChange={(event) => setCommentBody(event.target.value)}
                placeholder="Deixe um comentário..."
                rows={3}
                required
                maxLength={2000}
                className="w-full rounded-2xl border border-dune-border bg-white px-4 py-3 font-sans text-sm text-dune-text outline-none placeholder:text-[#C4C4CC] focus:border-dune-accent focus:ring-2 focus:ring-dune-accent/20"
              />

              {commentMutation.isError ? (
                <p className="text-sm text-signal-red">
                  {(commentMutation.error as Error).message}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={commentMutation.isPending}
                className="self-start rounded-2xl bg-dune-accent px-5 py-3 font-display text-sm font-medium text-white transition-colors hover:bg-[#E67700] disabled:bg-dune-border disabled:text-[#9CA3AF]"
              >
                {commentMutation.isPending ? 'Enviando...' : 'Comentar'}
              </button>
            </form>

            <div className="mt-4 flex flex-col gap-3">
              {detailQuery.data.comments.map((comment) => (
                <div
                  key={comment.id}
                  className="rounded-2xl border border-dune-border bg-white p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display text-sm font-medium text-dune-text">
                      {comment.authorName ?? 'Anônimo'}
                    </span>
                    <span className="text-xs text-dune-text-secondary">
                      {formatRelativeTime({ iso: comment.createdAt })}
                    </span>
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-dune-text-secondary">
                    {comment.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </main>
  )
}
